import * as React from 'react';

import { Color, parseColor } from '@react-stately/color';
import { cva } from 'class-variance-authority';
import { type ButtonProps as AriaButtonProps } from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { ThemeColor } from '../../../types/theme';
import { Button, type ButtonProps } from '../buttons/Button';
import { TextField } from '../forms/TextField';
import { Popover, PopoverDialog, PopoverTrigger } from '../overlays/Popover';

import {
  ColorArea,
  ColorField,
  ColorPicker as BaseColorPicker,
  ColorSlider,
  ColorThumb,
  ColorSliderTrack as SliderTrack,
} from './ColorPrimitives';

const colorButtonVariants = cva(
  'rounded-md border border-neutral/20 shadow-sm',
  {
    variants: {
      size: {
        default: 'size-4',
        small: 'size-2',
        large: 'size-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

// Base props shared by all color picker variant components
interface BaseColorPickerVariantProps {
  value: Color;
  onChange: (color: Color) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

// Full-featured color picker
const DefaultColorPicker = ({
  value,
  onChange,
  disabled,
  readOnly,
}: BaseColorPickerVariantProps) => {
  const containerClassName = cn(
    'flex gap-4',
    disabled && 'pointer-events-none opacity-50',
    readOnly && 'pointer-events-none',
  );

  return (
    <div className="@DefaultColorPicker contents">
      <BaseColorPicker value={value} onChange={onChange}>
        <div className={containerClassName}>
          <ColorArea className="col-start-1 row-span-5">
            <ColorThumb />
          </ColorArea>
          <div className="flex flex-col gap-2">
            <ColorSlider channel="hue">
              <SliderTrack>
                <ColorThumb className="mt-3" />
              </SliderTrack>
            </ColorSlider>
            <ColorSlider channel="saturation">
              <SliderTrack>
                <ColorThumb className="mt-3" />
              </SliderTrack>
            </ColorSlider>
            <ColorSlider channel="lightness">
              <SliderTrack>
                <ColorThumb className="mt-3" />
              </SliderTrack>
            </ColorSlider>
            <ColorSlider channel="alpha">
              <SliderTrack>
                <ColorThumb className="mt-3" />
              </SliderTrack>
            </ColorSlider>
            <div className="grow" />
            <ColorField colorSpace="rgb" className="w-[192px]">
              <TextField />
            </ColorField>
          </div>
        </div>
      </BaseColorPicker>
    </div>
  );
};

/**
 * Props for the ColorPickerButton component.
 */
interface ColorPickerButtonProps extends Omit<
  AriaButtonProps,
  'children' | 'value'
> {
  /**
   * Theme color for the button styling
   */
  color?: ThemeColor;

  /**
   * Visual style variant for the button
   * @default 'outline'
   */
  buttonVariant?: ButtonProps['variant'];

  /**
   * Variant of the color picker
   * @default 'default'
   */
  variant?: 'default';

  /**
   * Size of the color button
   * @default 'default'
   */
  size?: 'default' | 'small' | 'large';

  /**
   * Currently selected color (as string or Color object)
   */
  value?: string | Color;

  /**
   * Default color value when uncontrolled
   * @default 'hsl(0, 100%, 50%)'
   */
  defaultValue?: string | Color;

  /**
   * Callback fired when color changes
   */
  onChange?: (color: Color) => void;

  /**
   * Placement of the color picker popover
   * @default 'bottom'
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Distance in pixels between the button and the popover
   * @default 4
   */
  offset?: number;

  /**
   * Additional CSS class for the button
   */
  className?: string;

  /**
   * Additional CSS class for the popover
   */
  popoverClassName?: string;

  /**
   * Additional CSS class for the dialog
   */
  dialogClassName?: string;

  /**
   * Whether the color picker is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the color picker is read-only
   * @default false
   */
  readOnly?: boolean;

  /**
   * Whether to show the color code next to the button
   * @default false
   */
  showColorCode?: boolean;
}

/**
 * Button that opens a color picker in a popover.
 *
 * Displays a color swatch with an optional color code and opens a full-featured
 * color picker when clicked. Supports controlled and uncontrolled usage.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ColorPickerButton onChange={color => console.log(color.toString())} />
 *
 * // Controlled usage
 * <ColorPickerButton
 *   value={color}
 *   onChange={setColor}
 *   showColorCode
 * />
 *
 * // Custom styling
 * <ColorPickerButton
 *   buttonVariant="outline"
 *   size="large"
 *   placement="bottom"
 * />
 * ```
 *
 * @param props - ColorPickerButton properties
 */
const ColorPickerButton = ({
  className,
  buttonVariant = 'outline',
  color,
  variant: _variant = 'default',
  size = 'default',
  value,
  defaultValue = 'hsl(0, 100%, 50%)',
  onChange,
  placement = 'bottom',
  offset = 4,
  popoverClassName,
  dialogClassName,
  disabled = false,
  readOnly = false,
  showColorCode = false,
  ...props
}: ColorPickerButtonProps) => {
  const [localColor, setLocalColor] = React.useState<Color>(
    value
      ? typeof value === 'string'
        ? parseColor(value)
        : value
      : typeof defaultValue === 'string'
        ? parseColor(defaultValue)
        : defaultValue,
  );

  const handleColorChange = (newColor: Color) => {
    setLocalColor(newColor);
    onChange?.(newColor);
  };

  // For controlled component
  if (value && typeof value !== 'string' && value !== localColor) {
    setLocalColor(value);
  } else if (
    value &&
    typeof value === 'string' &&
    value !== localColor.toString()
  ) {
    setLocalColor(parseColor(value));
  }

  return (
    <PopoverTrigger>
      <Button
        variant={buttonVariant}
        color={color}
        size={size}
        className={cn(className, showColorCode && 'flex items-center gap-2')}
        isDisabled={disabled}
        {...props}
      >
        <div
          className={colorButtonVariants({ size })}
          style={{ backgroundColor: localColor.toString() }}
        />
        {showColorCode && (
          <span className="font-mono text-xs">{localColor.toString()}</span>
        )}
      </Button>
      <Popover
        placement={placement}
        offset={offset}
        className={cn('p-4', popoverClassName)}
        isOpen={readOnly || disabled ? false : undefined}
      >
        <PopoverDialog className={dialogClassName}>
          <DefaultColorPicker
            value={localColor}
            onChange={handleColorChange}
            disabled={disabled}
            readOnly={readOnly}
          />
        </PopoverDialog>
      </Popover>
    </PopoverTrigger>
  );
};

export { ColorPickerButton };
export type { ColorPickerButtonProps };
